/**
 * Azure DevOps stores System.Description as HTML from its rich-text editor, but the
 * detail modal edits it as plain text.
 *
 * The project has no HTML sanitizer, so DevOps content is never rendered as markup —
 * it is converted to text. That is lossy for genuinely rich descriptions, which is why
 * hasRichFormatting() exists: the modal warns before letting someone flatten one.
 */

/** Tags we can faithfully round-trip through plain text. */
const SIMPLE_TAGS = /^(div|p|br|span)$/i;

/**
 * Markup that plain-text editing would destroy. Images are NOT in this list: saving
 * re-appends a field's <img> tags verbatim (see inlineImageTags), so an image-only
 * description is safe to edit as plain text.
 */
const RICH_TAG = /<\s*(ul|ol|li|table|tr|td|th|a|b|strong|i|em|u|h[1-6]|pre|code|blockquote)\b/i;

const IMG_TAG = /<\s*img\b[^>]*>/gi;

export function htmlToPlainText(html: string): string {
  if (!html) return '';

  return html
    // Block boundaries become line breaks before tags are stripped.
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\s*\/\s*(p|div|li|tr|h[1-6])\s*>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    // &amp; must be decoded last, or "&amp;lt;" would wrongly become "<".
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&amp;/gi, '&')
    // DevOps emits a lot of empty wrappers; don't inherit their blank lines.
    .replace(/\n{2,}/g, '\n')
    .trim();
}

export function plainTextToHtml(text: string): string {
  if (!text) return '';

  return text
    // Ampersand first: escaping it after the others would double-escape them.
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>');
}

/**
 * The <img> tags of a field, verbatim.
 *
 * Used when saving a plain-text edit: the flattened text loses every tag, so the
 * original image tags are re-appended and the screenshots survive the edit.
 */
export function inlineImageTags(html: string): string[] {
  if (!html) return [];
  return html.match(IMG_TAG) ?? [];
}

/** Append an <img> for a freshly uploaded attachment to a field's HTML. */
export function appendImageTag(html: string, url: string): string {
  // %-encode quotes (still a valid URL), then HTML-escape the ampersands.
  const safe = url.replace(/"/g, '%22').replace(/&/g, '&amp;');
  return `${html}<img src="${safe}">`;
}

/** An image pasted into a DevOps rich-text field, stored as a work-item attachment. */
export interface InlineImageRef {
  /** The attachment GUID — fetchable through our attachment download proxy. */
  id: string;
  fileName: string;
}

const IMG_SRC = /<img\b[^>]*\ssrc\s*=\s*"([^"]+)"/gi;
const ATTACHMENT_GUID = /\/_apis\/wit\/attachments\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

/**
 * Screenshots pasted into a DevOps description/repro-steps field.
 *
 * They live in the HTML as <img> tags pointing at attachment URLs the browser cannot
 * load (they need DevOps credentials), and htmlToPlainText() drops them entirely — so
 * they are pulled out here and rendered separately through the backend proxy.
 */
export function extractInlineImages(html: string): InlineImageRef[] {
  if (!html) return [];

  const seen = new Set<string>();
  const images: InlineImageRef[] = [];

  for (const match of html.matchAll(IMG_SRC)) {
    // Attribute values are HTML-escaped; &amp; hides the query-string separator.
    const src = match[1].replace(/&amp;/gi, '&');
    const guid = ATTACHMENT_GUID.exec(src)?.[1];
    if (!guid || seen.has(guid)) continue;
    seen.add(guid);

    const rawName = /[?&]fileName=([^&]+)/i.exec(src)?.[1];
    let fileName = 'image';
    if (rawName) {
      try {
        fileName = decodeURIComponent(rawName);
      } catch {
        fileName = rawName;
      }
    }
    images.push({ id: guid, fileName });
  }
  return images;
}

/**
 * Whether this description holds formatting that plain-text editing would flatten.
 * Simple div/p/br/span wrappers — what DevOps puts around ordinary typing — don't count.
 */
export function hasRichFormatting(html: string): boolean {
  if (!html) return false;
  // Images are preserved across a plain-text edit, so they don't count as rich.
  const withoutImages = html.replace(IMG_TAG, '');
  if (RICH_TAG.test(withoutImages)) return true;

  // Any styled wrapper also carries formatting worth protecting.
  if (/<[^>]+\sstyle\s*=/i.test(withoutImages)) return true;

  const tags = withoutImages.match(/<\s*\/?\s*([a-z0-9]+)/gi) ?? [];
  return tags.some(t => {
    const name = t.replace(/[<\/\s]/g, '');
    return name && !SIMPLE_TAGS.test(name);
  });
}
